import React, { useState, useEffect } from 'react';
import { 
    collection, doc, getDoc, updateDoc, deleteDoc, addDoc, setDoc, 
    writeBatch, onSnapshot, query, where 
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage, appId } from '../firebase';
import { useAuth } from '../context/AuthContext';
import MessageBox from '../components/MessageBox';
import { generateTimeSlots, getComputedDisplayName, getCalculatedAge } from '../utils/helpers';
import { NOT_AVAILABLE_URL } from '../utils/constants';

// --- SUB-COMPONENT: AdminUserLookup ---
function AdminUserLookup({ allUsers }) {
    const [selectedUserId, setSelectedUserId] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    
    useEffect(() => {
        if (selectedUserId) {
            setSelectedUser(allUsers.find(u => u.id === selectedUserId));
        } else {
            setSelectedUser(null);
        }
    }, [selectedUserId, allUsers]);

    const userOptions = allUsers.sort((a, b) => a.displayName.localeCompare(b.displayName));
    
    const getPetDocumentUrl = (doc) => {
        return doc.url || '#';
    };

    return (
        <div id="section-user-lookup" className="mb-8 p-6 border border-gray-200 rounded-lg shadow-sm bg-yellow-50 scroll-mt-24">
            <h3 className="text-2xl font-bold text-yellow-800 mb-4 text-center">Admin: User Profile Lookup</h3>
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4 mb-6">
                <label htmlFor="userLookupSelect" className="text-lg font-medium text-gray-700">Select User to View:</label>
                <select
                    id="userLookupSelect"
                    value={selectedUserId}
                    onChange={(e) => setSelectedUserId(e.target.value)}
                    className="p-2 border border-gray-300 rounded-md text-lg focus:ring-yellow-500 focus:border-yellow-500 bg-white"
                >
                    <option value="">-- Select a User --</option>
                    {userOptions.map(user => (
                        <option key={user.id} value={user.id}>
                            {user.displayName} ({user.email}) 
                            {user.isActive === false && ' (Deactivated)'}
                        </option>
                    ))}
                </select>
            </div>

            {selectedUser && (
                <div className="mt-8 p-6 bg-white rounded-lg shadow-lg">
                    <h4 className="text-2xl font-bold text-gray-800 mb-4 border-b pb-2">
                        Profile Details for <span style={{ color: selectedUser.color }}>{selectedUser.displayName}</span>
                    </h4>
                    <div className="mb-4 flex items-center space-x-3">
                        {selectedUser.isAdmin && (
                            <span className="px-3 py-1 bg-yellow-400 text-yellow-900 text-sm font-extrabold rounded-full">ADMIN</span>
                        )}
                        <p><span className="font-semibold">Status:</span> 
                            <span className={`font-extrabold ml-1 ${selectedUser.isActive === false ? 'text-red-500' : 'text-green-500'}`}>
                                {selectedUser.isActive === false ? 'DEACTIVATED' : 'ACTIVE'}
                            </span>
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-4 text-gray-700">
                        <div className="space-y-2">
                            <p><span className="font-semibold">Full Name:</span> {selectedUser.firstName} {selectedUser.lastName}</p>
                            <p><span className="font-semibold">Email:</span> {selectedUser.email}</p>
                            <p><span className="font-semibold">Phone:</span> {selectedUser.cellPhoneNumber || 'N/A'}</p>
                            <p><span className="font-semibold">Address:</span> {selectedUser.homeAddress || 'N/A'}</p>
                            <p><span className="font-semibold">Available Spots:</span> <span className="font-extrabold text-blue-600">{selectedUser.availableSpots || 0}</span></p>
                        </div>
                        <div className="space-y-2 border-t md:border-t-0 pt-4 md:pt-0">
                            <p className="font-semibold text-lg text-red-600 mb-2">Emergency Contact</p>
                            <p><span className="font-semibold">Name:</span> {selectedUser.emergencyContactName || 'N/A'}</p>
                            <p><span className="font-semibold">Phone:</span> {selectedUser.emergencyContactPhone || 'N/A'}</p>
                        </div>
                    </div>

                    <h4 className="text-2xl font-bold text-gray-800 mt-8 mb-4 border-b pb-2">Registered Pets ({selectedUser.pets?.length || 0})</h4>
                    {selectedUser.pets && selectedUser.pets.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {selectedUser.pets.map((pet, index) => (
                                <div key={pet.id || index} className="bg-gray-50 p-4 rounded-lg shadow-inner border border-gray-200">
                                    {pet.pictureUrl ? (
                                        <img src={pet.pictureUrl} alt={pet.name} className="w-20 h-20 rounded-full object-cover mb-3 border-2 border-yellow-500 mx-auto" onError={(e) => { e.target.onerror = null; e.target.src = NOT_AVAILABLE_URL; }}/>
                                    ) : (
                                        <div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 text-xs mb-3 mx-auto">No Photo</div>
                                    )}
                                    <p className="font-bold text-lg text-center text-gray-800">{pet.name}</p>
                                    <p className="text-sm"><span className="font-semibold">Type/Breed:</span> {pet.type} / {pet.breed}</p>
                                    <p className="text-sm"><span className="font-semibold">Gender:</span> {pet.gender}</p>
                                    <p className="text-sm"><span className="font-semibold">Born:</span> {pet.dob || 'N/A'} ({getCalculatedAge(pet.dob)})</p>
                                    {pet.specialNeeds && <p className="text-sm text-red-600 font-bold mt-1">Needs: {pet.specialNeeds}</p>}
                                    
                                    {pet.documents && pet.documents.length > 0 && (
                                        <div className="mt-3 pt-2 border-t border-gray-100">
                                            <p className="font-semibold text-sm">Documents:</p>
                                            <ul className="list-disc list-inside text-xs">
                                                {pet.documents.map((doc, docIndex) => (
                                                    <li key={docIndex}>
                                                        <a href={getPetDocumentUrl(doc)} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline truncate" title={doc.name}>
                                                            {doc.name}
                                                        </a>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-gray-600 italic">This user has not registered any pets yet.</p>
                    )}
                </div>
            )}
        </div>
    );
}

// --- SUB-COMPONENT: AdminShopManager (UPDATED WITH EDIT) ---
function AdminShopManager() {
    // eslint-disable-next-line no-unused-vars
    const { currentUser } = useAuth();
    const [products, setProducts] = useState([]);
    const [orders, setOrders] = useState([]);
    
    // Form States
    const [newName, setNewName] = useState('');
    const [newDesc, setNewDesc] = useState('');
    const [newPrice, setNewPrice] = useState('');
    const [newImageFile, setNewImageFile] = useState(null);
    const [productToEdit, setProductToEdit] = useState(null); // Tracks edit mode
    
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('info');

    useEffect(() => {
        if (!db) return;
        const prodRef = collection(db, 'artifacts', appId, 'public', 'data', 'products');
        const unsubProd = onSnapshot(prodRef, (snap) => {
            setProducts(snap.docs.map(d => ({id: d.id, ...d.data()})));
        });
        const orderRef = collection(db, 'artifacts', appId, 'public', 'data', 'productOrders');
        const qOrders = query(orderRef, where('status', '==', 'pending'));
        const unsubOrd = onSnapshot(qOrders, (snap) => {
            setOrders(snap.docs.map(d => ({id: d.id, ...d.data(), orderedAt: d.data().orderedAt?.toDate()})));
        });
        return () => { unsubProd(); unsubOrd(); };
    }, []);

    const handleSaveProduct = async (e) => {
        e.preventDefault();
        if (!newName || !newPrice) {
            setMessage("Name and Price are required."); setMessageType('error'); return;
        }
        setLoading(true);
        try {
            let imageUrl = productToEdit ? productToEdit.imageUrl : '';
            let storagePath = productToEdit ? productToEdit.storagePath : '';

            // Handle Image Upload
            if (newImageFile) {
                const fileName = `${Date.now()}_${newImageFile.name}`;
                storagePath = `artifacts/${appId}/public/products/${fileName}`;
                const storageRef = ref(storage, storagePath);
                await uploadBytes(storageRef, newImageFile);
                imageUrl = await getDownloadURL(storageRef);
            }

            const productData = {
                name: newName,
                description: newDesc,
                price: parseFloat(newPrice),
                imageUrl,
                storagePath
            };

            if (productToEdit) {
                // Update Existing
                await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'products', productToEdit.id), productData);
                setMessage("Product updated successfully!");
            } else {
                // Create New
                await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'products'), {
                    ...productData,
                    status: 'active',
                    createdAt: new Date()
                });
                setMessage("Product added successfully!");
            }
            
            setMessageType('info');
            handleCancelEdit(); // Reset form
        } catch (err) {
            setMessage("Error: " + err.message); setMessageType('error');
        } finally { setLoading(false); }
    };

    const handleEditClick = (product) => {
        setProductToEdit(product);
        setNewName(product.name);
        setNewDesc(product.description);
        setNewPrice(product.price);
        setNewImageFile(null);
        // Scroll to form to make it visible
        const formElement = document.getElementById('shop-form');
        if(formElement) formElement.scrollIntoView({behavior: 'smooth'});
        setMessage('');
    };

    const handleCancelEdit = () => {
        setProductToEdit(null);
        setNewName(''); 
        setNewDesc(''); 
        setNewPrice(''); 
        setNewImageFile(null);
        // Reset file input value if possible
        const fileInput = document.getElementById('productImageInput');
        if(fileInput) fileInput.value = "";
    };

    const handleDeleteProduct = async (product) => {
        if(!window.confirm("Delete " + product.name + "?")) return;
        setLoading(true);
        try {
            if (product.storagePath) {
                await deleteObject(ref(storage, product.storagePath)).catch(e => console.log("Img delete fail", e));
            }
            await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'products', product.id));
            setMessage("Product deleted."); setMessageType('info');
            if(productToEdit?.id === product.id) handleCancelEdit();
        } catch(e) { setMessage(e.message); setMessageType('error'); }
        finally { setLoading(false); }
    };

    const handleToggleStatus = async (product) => {
        const newStatus = product.status === 'active' ? 'inactive' : 'active';
        await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'products', product.id), { status: newStatus });
    };

    const handleProcessOrder = async (orderId, status) => {
        try {
            await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'productOrders', orderId), { status });
            setMessage(`Order marked as ${status}`); setMessageType('info');
        } catch (e) { setMessage(e.message); setMessageType('error'); }
    };

    return (
        <div id="section-shop" className="mb-8 p-6 border border-emerald-200 rounded-lg shadow-sm bg-emerald-50 scroll-mt-24">
            <MessageBox message={message} type={messageType} onClose={() => setMessage('')} />
            <h3 className="text-2xl font-bold text-emerald-800 mb-6 text-center">Admin: Shop Manager</h3>

            <div className="mb-8 bg-white p-4 rounded shadow">
                <h4 className="font-bold text-lg mb-4">Pending Shop Orders</h4>
                {orders.length === 0 ? <p className="text-gray-500 italic">No pending orders.</p> : (
                    <table className="w-full text-sm">
                        <thead><tr className="bg-gray-100"><th className="p-2 text-left">User</th><th className="p-2 text-left">Item</th><th className="p-2">Price</th><th className="p-2">Actions</th></tr></thead>
                        <tbody>
                            {orders.map(o => (
                                <tr key={o.id} className="border-b">
                                    <td className="p-2">{o.userName}</td>
                                    <td className="p-2">{o.productName}</td>
                                    <td className="p-2">R {o.price}</td>
                                    <td className="p-2 flex space-x-2">
                                        <button onClick={() => handleProcessOrder(o.id, 'approved')} className="bg-green-500 text-white px-2 py-1 rounded hover:bg-green-600">Approve</button>
                                        <button onClick={() => handleProcessOrder(o.id, 'rejected')} className="bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600">Reject</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <div id="shop-form" className="bg-white p-4 rounded shadow mb-8 border-2 border-emerald-100">
                <h4 className="font-bold text-lg mb-4 text-emerald-700">
                    {productToEdit ? `Edit Product: ${productToEdit.name}` : 'Add New Product'}
                </h4>
                <form onSubmit={handleSaveProduct} className="grid gap-4 md:grid-cols-2">
                    <input className="border p-2 rounded" placeholder="Product Name" value={newName} onChange={e=>setNewName(e.target.value)} required />
                    <input className="border p-2 rounded" placeholder="Price (R)" type="number" value={newPrice} onChange={e=>setNewPrice(e.target.value)} required />
                    <textarea className="border p-2 rounded md:col-span-2" placeholder="Description" value={newDesc} onChange={e=>setNewDesc(e.target.value)} rows="2" />
                    <div className="md:col-span-2">
                        <label className="block text-sm text-gray-600 mb-1">
                            {productToEdit ? 'Change Image (Optional):' : 'Product Image:'}
                        </label>
                        <input id="productImageInput" type="file" onChange={e=>setNewImageFile(e.target.files[0])} accept="image/*" />
                        {productToEdit && productToEdit.imageUrl && !newImageFile && (
                            <p className="text-xs text-gray-500 mt-1">Currently using existing image.</p>
                        )}
                    </div>
                    <div className="md:col-span-2 flex space-x-3">
                        <button disabled={loading} className="flex-1 bg-emerald-600 text-white p-2 rounded font-bold hover:bg-emerald-700">
                            {loading ? 'Saving...' : (productToEdit ? 'Update Product' : 'Add Product')}
                        </button>
                        {productToEdit && (
                            <button type="button" onClick={handleCancelEdit} className="flex-1 bg-gray-400 text-white p-2 rounded font-bold hover:bg-gray-500">
                                Cancel Edit
                            </button>
                        )}
                    </div>
                </form>
            </div>

            <div className="bg-white p-4 rounded shadow">
                <h4 className="font-bold text-lg mb-4">Manage Products</h4>
                <div className="space-y-2">
                    {products.map(p => (
                        <div key={p.id} className={`flex items-center justify-between border p-2 rounded hover:bg-gray-50 ${productToEdit?.id === p.id ? 'bg-blue-50 border-blue-300' : ''}`}>
                            <div className="flex items-center space-x-3">
                                <img src={p.imageUrl || "https://placehold.co/50"} alt="" className="w-12 h-12 object-cover rounded bg-gray-200" />
                                <div>
                                    <p className="font-bold">{p.name} <span className="text-xs font-normal text-gray-500">({p.status})</span></p>
                                    <p className="text-xs text-gray-600">R {p.price}</p>
                                </div>
                            </div>
                            <div className="flex space-x-2 text-sm">
                                <button onClick={() => handleEditClick(p)} className="text-yellow-600 hover:text-yellow-800 font-semibold underline">Edit</button>
                                <button onClick={() => handleToggleStatus(p)} className="text-blue-600 hover:text-blue-800 underline">{p.status==='active'?'Disable':'Enable'}</button>
                                <button onClick={() => handleDeleteProduct(p)} className="text-red-600 hover:text-red-800 underline">Delete</button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

// --- SUB-COMPONENT: ManageUserSpots ---
function ManageUserSpots() {
    const { userDetails } = useAuth();
    const [allUsers, setAllUsers] = useState([]); 
    const [userAdjustments, setUserAdjustments] = useState({}); 
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('info');
    const [loading, setLoading] = useState(false);
    const [loadingUsers, setLoadingUsers] = useState(true);
    const [userToToggle, setUserToToggle] = useState(null);
    const [editingMembershipUser, setEditingMembershipUser] = useState(null);
    const [newExpiryDate, setNewExpiryDate] = useState('');

    useEffect(() => {
        if (!userDetails?.isAdmin || !db) {
            setLoadingUsers(false);
            return;
        };

        const usersColRef = collection(db, 'artifacts', appId, 'users');
        const q = query(usersColRef); 
        
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const fetchedUsers = snapshot.docs.map(doc => {
                 const data = doc.data();
                 const displayName = getComputedDisplayName(data); 
                 return { 
                     id: doc.id, 
                     ...data,
                     displayName: displayName,
                     currentSpots: data.availableSpots || 0,
                     isActive: data.isActive !== false,
                     isGateCodeBlocked: data.isGateCodeBlocked === true
                 };
            });
            
            fetchedUsers.sort((a, b) => {
                if (a.isActive === b.isActive) {
                    return a.displayName.localeCompare(b.displayName);
                }
                return a.isActive ? -1 : 1;
            });
            
            setAllUsers(fetchedUsers);
            setLoadingUsers(false);
        }, (error) => {
            console.error("Error fetching users for admin:", error);
            setMessage(`Could not load user list: ${error.message}`);
            setMessageType("error");
            setLoadingUsers(false);
        });
        return () => unsubscribe();
    }, [userDetails]);

    const handleAdjustmentChange = (userId, delta) => {
        setUserAdjustments(prev => {
            const currentAdjustment = prev[userId] || 0;
            const newAdjustment = currentAdjustment + delta;
            if (newAdjustment === 0) {
                const newState = { ...prev };
                delete newState[userId];
                return newState;
            }
            return { ...prev, [userId]: newAdjustment };
        });
    };

    const handleApplyAdjustment = async (userId) => {
        const adjustment = userAdjustments[userId];
        if (!adjustment || adjustment === 0) return;
        
        const userToUpdate = allUsers.find(u => u.id === userId);
        if (!userToUpdate) return;

        setMessage('');
        setLoading(true);

        try {
            const userDocRef = doc(db, 'artifacts', appId, 'users', userId);
            const currentSpots = userToUpdate.currentSpots;
            const newSpots = currentSpots + adjustment;

            if (newSpots < 0) {
                setMessage(`Cannot remove spots. Result would be negative.`);
                setMessageType('error');
                setLoading(false);
                return;
            }
            
            await updateDoc(userDocRef, { availableSpots: newSpots });
            
            setUserAdjustments(prev => {
                const newState = { ...prev };
                delete newState[userId];
                return newState;
            });
            
            setMessage(`Adjusted spots for ${userToUpdate.displayName}. New total: ${newSpots}`);
            setMessageType('info');
        } catch (error) {
            console.error("Error adjusting spots:", error);
            setMessage(`Error: ${error.message}`);
            setMessageType('error');
        } finally {
            setLoading(false);
        }
    };
    
    const handleStatusToggleClick = (user) => {
        setUserToToggle(user);
    };

    const handleConfirmStatusChange = async () => {
        if (!userToToggle || !db) return;
        setMessage('');
        setLoading(true);
        const isDeactivating = userToToggle.isActive; 
        try {
            const userDocRef = doc(db, 'artifacts', appId, 'users', userToToggle.id);
            const updates = { 
                isActive: !isDeactivating, 
                deactivatedAt: isDeactivating ? new Date() : null 
            };
            if (isDeactivating) {
                updates.availableSpots = 0;
            }
            await updateDoc(userDocRef, updates);
            setMessage(`User ${userToToggle.displayName} successfully ${isDeactivating ? 'DEACTIVATED' : 'REACTIVATED'}.`);
            setMessageType(isDeactivating ? 'info' : 'success');
        } catch (error) {
            console.error("Error changing user status:", error);
            setMessage(`Error: ${error.message}`);
            setMessageType('error');
        } finally {
            setLoading(false);
            setUserToToggle(null);
        }
    };

    const handleCancelStatusChange = () => {
        setUserToToggle(null);
    };

    const handleGateCodeBlockToggle = async (user) => {
        if (!db) return;
        setLoading(true);
        const newBlockedStatus = !user.isGateCodeBlocked;
        try {
            const userDocRef = doc(db, 'artifacts', appId, 'users', user.id);
            await updateDoc(userDocRef, { isGateCodeBlocked: newBlockedStatus });
            setMessage(`Gate Code display ${newBlockedStatus ? 'BLOCKED' : 'ALLOWED'} for ${user.displayName}.`);
            setMessageType(newBlockedStatus ? 'info' : 'success');
        } catch (error) {
            console.error("Error toggling gate code block:", error);
            setMessage(`Error: ${error.message}`);
            setMessageType('error');
        } finally {
            setLoading(false);
        }
    };

    const handleEditMembershipClick = (user) => {
        setEditingMembershipUser(user);
        if (user.membershipExpiresAt) {
            try {
                const date = user.membershipExpiresAt.toDate();
                const isoString = date.toISOString().split('T')[0];
                setNewExpiryDate(isoString);
            } catch(e) {
                setNewExpiryDate('');
            }
        } else {
            setNewExpiryDate('');
        }
    };

    const handleSaveMembershipDate = async () => {
        if (!editingMembershipUser) return;
        setLoading(true);
        try {
            const userDocRef = doc(db, 'artifacts', appId, 'users', editingMembershipUser.id);
            let dateToSave = null;
            if (newExpiryDate) {
                dateToSave = new Date(newExpiryDate);
                dateToSave.setHours(23, 59, 59, 999);
            }
            await updateDoc(userDocRef, { membershipExpiresAt: dateToSave });
            setMessage(`Updated membership expiry for ${editingMembershipUser.displayName}.`);
            setMessageType('success');
            setEditingMembershipUser(null);
        } catch (error) {
            console.error("Error updating membership:", error);
            setMessage(`Error: ${error.message}`);
            setMessageType('error');
        } finally {
            setLoading(false);
        }
    };

    // Row Component
    const UserAdjustmentRow = React.memo(({ user }) => {
        const adjustment = userAdjustments[user.id] || 0;
        const newSpotTotal = user.currentSpots + adjustment;
        const isAdjustmentPending = adjustment !== 0;

        const expiryDate = user.membershipExpiresAt ? user.membershipExpiresAt.toDate() : null;
        const isExpired = !expiryDate || new Date() > expiryDate;

        return (
            <tr key={user.id} className={`border-b last:border-b-0 hover:bg-gray-50 ${!user.isActive ? 'bg-gray-100' : 'bg-white'}`}>
                <td className="py-3 px-4 text-center">
                        <button
                        onClick={() => handleStatusToggleClick(user)}
                        disabled={loading}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                            user.isActive ? 'bg-green-500' : 'bg-gray-300'
                        }`}
                        title={user.isActive ? "Click to Deactivate" : "Click to Reactivate"}
                    >
                        <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                user.isActive ? 'translate-x-6' : 'translate-x-1'
                            }`}
                        />
                    </button>
                    <div className="text-[10px] font-bold mt-1 text-gray-500">
                        {user.isActive ? 'ACTIVE' : 'INACTIVE'}
                    </div>
                </td>

                <td className="py-3 px-4 text-gray-800 text-sm font-semibold truncate max-w-[200px]" title={user.email}>
                    <span style={{ color: user.color }}>{user.displayName}</span>
                    {user.isAdmin && (
                        <span className="ml-2 px-2 py-0.5 bg-yellow-200 text-yellow-800 text-xs font-bold rounded-full shadow-sm">ADMIN</span>
                    )}
                    <span className="block text-xs text-gray-400 font-normal truncate">{user.email}</span>
                </td>

                <td className="py-3 px-4 text-center text-sm">
                    <div className="flex flex-col items-center justify-center">
                        {expiryDate ? (
                            <>
                                <div className={isExpired ? "text-red-600 font-bold" : "text-green-600 font-bold"}>
                                    {expiryDate.toLocaleDateString()}
                                </div>
                                {isExpired && <span className="text-[10px] text-red-500">EXPIRED</span>}
                            </>
                        ) : (
                            <span className="text-gray-400 italic">None</span>
                        )}
                        <button 
                            onClick={() => handleEditMembershipClick(user)}
                            className="mt-1 text-xs text-blue-600 hover:text-blue-800 underline font-semibold"
                        >
                            Edit Date
                        </button>
                    </div>
                </td>

                <td className="py-3 px-4 text-center">
                    <button
                        onClick={() => handleGateCodeBlockToggle(user)}
                        disabled={loading}
                        className={`px-3 py-1 text-xs font-bold rounded shadow-sm border ${
                            user.isGateCodeBlocked 
                            ? 'bg-red-100 text-red-700 border-red-300 hover:bg-red-200' 
                            : 'bg-green-100 text-green-700 border-green-300 hover:bg-green-200'
                        }`}
                        title={user.isGateCodeBlocked ? "Gate Code is HIDDEN for this user" : "Gate Code is VISIBLE to this user"}
                    >
                        {user.isGateCodeBlocked ? "BLOCKED" : "ALLOWED"}
                    </button>
                </td>

                <td className="py-3 px-4 text-center text-lg font-extrabold text-blue-600">
                    {user.currentSpots}
                </td>
                <td className="py-3 px-4">
                    <div className="flex items-center space-x-2 justify-center">
                        <button
                            onClick={() => handleAdjustmentChange(user.id, -1)}
                            disabled={loading || newSpotTotal <= 0} 
                            className={`p-2 rounded-full font-bold transition duration-200 shadow-sm text-white ${
                                newSpotTotal <= 0 ? 'bg-gray-300 cursor-not-allowed' : 'bg-red-500 hover:bg-red-600'
                            }`}
                        >
                            &minus;
                        </button>
                        
                        <span className={`px-4 py-1 rounded-md font-bold min-w-[50px] text-center ${
                            adjustment > 0 ? 'bg-green-100 text-green-800' : adjustment < 0 ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-600'
                        }`}>
                            {adjustment > 0 ? `+${adjustment}` : adjustment}
                        </span>

                        <button
                            onClick={() => handleAdjustmentChange(user.id, 1)}
                            disabled={loading}
                            className="p-2 rounded-full bg-green-500 text-white font-bold hover:bg-green-600 transition duration-200 shadow-sm"
                        >
                            &#43;
                        </button>
                    </div>
                </td>
                <td className="py-3 px-4 text-center">
                    <span className={`text-lg font-extrabold ${newSpotTotal < 0 ? 'text-red-500' : 'text-purple-600'}`}>
                        {newSpotTotal}
                    </span>
                </td>
                <td className="py-3 px-4 text-center">
                    <button
                        onClick={() => handleApplyAdjustment(user.id)}
                        disabled={loading || !isAdjustmentPending}
                        className={`px-4 py-2 text-sm font-semibold rounded-lg transition duration-200 ${
                            isAdjustmentPending ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        }`}
                    >
                        Apply
                    </button>
                </td>
            </tr>
        );
    });

    if (!userDetails?.isAdmin) return null;

    return (
        <div id="section-user-spots" className="mb-8 p-6 border border-gray-200 rounded-lg shadow-sm bg-yellow-50 scroll-mt-24">
            <MessageBox message={message} type={messageType} onClose={() => setMessage('')} />
            
            {userToToggle && (
                <MessageBox
                    message={
                        userToToggle.isActive 
                        ? `CONFIRM DEACTIVATION: Are you sure you want to deactivate ${userToToggle.displayName}?`
                        : `CONFIRM REACTIVATION: Are you sure you want to reactivate ${userToToggle.displayName}?`
                    }
                    onClose={handleCancelStatusChange}
                    type={userToToggle.isActive ? "error" : "info"}
                >
                    <div className="mt-4 flex justify-center space-x-4">
                        <button
                            onClick={handleConfirmStatusChange}
                            className={`px-6 py-2 text-white rounded-md font-bold ${
                                userToToggle.isActive ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'
                            }`}
                        >
                            Confirm
                        </button>
                        <button
                            onClick={handleCancelStatusChange}
                            className="px-6 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400"
                        >
                            Cancel
                        </button>
                    </div>
                </MessageBox>
            )}

            {editingMembershipUser && (
                <MessageBox
                    message={`Update Membership Expiry for ${editingMembershipUser.displayName}`}
                    onClose={() => setEditingMembershipUser(null)}
                >
                    <div className="mt-4 flex flex-col items-center">
                        <p className="text-sm text-gray-600 mb-2">Select the new last day of their membership:</p>
                        <input 
                            type="date" 
                            value={newExpiryDate} 
                            onChange={(e) => setNewExpiryDate(e.target.value)} 
                            className="p-2 border border-gray-300 rounded-md shadow-sm mb-4 w-full max-w-xs"
                        />
                        <div className="flex justify-center space-x-4">
                            <button
                                onClick={handleSaveMembershipDate}
                                className="px-6 py-2 bg-blue-600 text-white rounded-md font-bold hover:bg-blue-700"
                            >
                                Save Date
                            </button>
                            <button
                                onClick={() => setEditingMembershipUser(null)}
                                className="px-6 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400"
                            >
                                Cancel
                            </button>
                        </div>
                        <button 
                            onClick={() => { setNewExpiryDate(''); }} 
                            className="mt-2 text-xs text-red-500 underline"
                        >
                            Clear Membership (Remove Date)
                        </button>
                    </div>
                </MessageBox>
            )}

            <h3 className="text-2xl font-bold text-yellow-800 mb-4 text-center">Admin: Manage User Spots & Status</h3>
            <p className="text-gray-700 mb-6 text-center">Manage Active/Inactive status, Membership dates, Gate Code access, and Spot counts.</p>
            
            {loadingUsers ? (
                <div className="text-center py-8">
                        <div className="text-xl font-semibold text-gray-700">Loading user list...</div>
                </div>
            ) : allUsers.length === 0 ? (
                <p className="text-center py-8 text-lg text-gray-500">No users found in the database.</p>
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-full bg-white rounded-lg shadow-md border border-gray-200">
                        <thead className="bg-gray-100 border-b border-gray-200">
                            <tr>
                                <th className="py-3 px-4 text-center text-sm font-semibold text-gray-600 w-20">Status</th>
                                <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600 min-w-[150px]">User</th>
                                <th className="py-3 px-4 text-center text-sm font-semibold text-gray-600">Membership Ends</th>
                                <th className="py-3 px-4 text-center text-sm font-semibold text-gray-600">Code Access</th>
                                <th className="py-3 px-4 text-center text-sm font-semibold text-gray-600">Current Spots</th>
                                <th className="py-3 px-4 text-center text-sm font-semibold text-gray-600 min-w-[150px]">Adjust By (+/-)</th>
                                <th className="py-3 px-4 text-center text-sm font-semibold text-gray-600">New Total</th>
                                <th className="py-3 px-4 text-center text-sm font-semibold text-gray-600">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {allUsers.map(user => (
                                <UserAdjustmentRow key={user.id} user={user} />
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

// --- MAIN PAGE COMPONENT: BlockDatesPage ---
function BlockDatesPage({ bookingsDisabled, setBookingsDisabled, gateCode, blockedDays, bookings }) { 
    // eslint-disable-next-line no-unused-vars
    const { userDetails, currentUser } = useAuth();
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('info');
    const [loading, setLoading] = useState(false);
    const [newGateCode, setNewGateCode] = useState('');
    const [allUsers, setAllUsers] = useState([]); 
    
    // PACKAGE MANAGEMENT STATES:
    const [newPackageName, setNewPackageName] = useState('');
    const [newPackageSlots, setNewPackageSlots] = useState('');
    const [newPackageCost, setNewPackageCost] = useState(''); 
    const [newPackageDuration, setNewPackageDuration] = useState(''); 
    const [newPackageDescription, setNewPackageDescription] = useState('');
    const [allPackages, setAllPackages] = useState([]);
    const [pendingRequests, setPendingRequests] = useState([]);
    // eslint-disable-next-line no-unused-vars
    const [allApprovedPurchases, setAllApprovedPurchases] = useState([]); 
    const [packageToDelete, setPackageToDelete] = useState(null);
    const [packageToEdit, setPackageToEdit] = useState(null); 
    const [requestToReject, setRequestToReject] = useState(null);
    const [rejectionReason, setRejectionReason] = useState('');

    // --- BLOCK DATES/SLOTS STATES ---
    const [selectedDate, setSelectedDate] = useState(() => {
        const today = new Date();
        today.setHours(0,0,0,0);
        return today;
    });
    const [adminSelectedSlots, setAdminSelectedSlots] = useState([]); 
    const [blockReason, setBlockReason] = useState('');
    const [currentWeekStart, setCurrentWeekStart] = useState(() => {
        const today = new Date();
        const dayOfWeek = today.getDay();
        const diff = today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
        const monday = new Date(today.setDate(diff));
        monday.setHours(0, 0, 0, 0);
        return monday;
    });

    const getWeekDays = (startOfWeek) => {
        const days = [];
        for (let i = 0; i < 7; i++) {
            const day = new Date(startOfWeek);
            day.setDate(startOfWeek.getDate() + i);
            days.push(day);
        }
        return days;
    };

    const weekDays = getWeekDays(currentWeekStart);
    const allTimeSlots = generateTimeSlots(selectedDate);
    const pendingBookings = bookings.filter(b => b.status === 'pending');
    
    // SCROLL HELPER
    const scrollToSection = (id) => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const handleBookingAction = async (bookingId, newStatus) => {
        if (!userDetails?.isAdmin || !db) {
            setMessage("You are not authorized to perform this action.");
            setMessageType('error');
            return;
        }
        setMessage('');
        try {
            const bookingDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'bookings', bookingId);
            const bookingDoc = await getDoc(bookingDocRef);
            if (!bookingDoc.exists()) {
                    setMessage("Booking not found.");
                    setMessageType('error');
                    return;
            }
            const bookingData = bookingDoc.data();
            const currentStatus = bookingData.status;
            const batch = writeBatch(db);
            
            if (newStatus === 'rejected' && currentStatus === 'pending') {
                const userDocRef = doc(db, 'artifacts', appId, 'users', bookingData.userId);
                const userDoc = await getDoc(userDocRef);
                if (userDoc.exists()) {
                    const currentSpots = userDoc.data().availableSpots || 0;
                    const newSpots = currentSpots + 1; 
                    batch.update(userDocRef, { availableSpots: newSpots });
                }
            }
            batch.update(bookingDocRef, { status: newStatus });
            await batch.commit();
            setMessage(`Booking ${newStatus} successfully.`);
            setMessageType('info');
        } catch (error) {
            console.error(`Error updating booking status to ${newStatus}:`, error);
            setMessage(`Error updating booking: ${error.message}`);
            setMessageType('error');
        }
    };
    
    // --- BLOCK DATES/SLOTS FUNCTIONS ---
    const getSlotBlockStatus = (slotTime) => {
        const now = new Date();
        if (slotTime.getTime() < now.getTime()) return { status: 'past' };
        const existingBlock = blockedDays.find(bs => 
            bs.startTime && bs.startTime.getTime() === slotTime.getTime()
        );
        if (existingBlock) return { status: 'blocked', reason: existingBlock.reason, id: existingBlock.id };
        const overlappingBooking = bookings.find(booking =>
            booking.startTime.getTime() === slotTime.getTime() &&
            booking.status === 'approved' 
        );
        if (overlappingBooking) return { status: 'booked', booking: overlappingBooking };
        const isSelected = adminSelectedSlots.some(s => s.getTime() === slotTime.getTime());
        if (isSelected) return { status: 'selected' };
        return { status: 'available' };
    };
    
    const handleAdminSlotClick = (slotTime) => {
        const statusInfo = getSlotBlockStatus(slotTime);
        if (statusInfo.status === 'booked' || statusInfo.status === 'past') {
            setMessage(`Slot is ${statusInfo.status === 'booked' ? 'booked' : 'past'}.`);
            setMessageType('error');
            return;
        }
        if (adminSelectedSlots.some(s => s.getTime() === slotTime.getTime())) {
            setAdminSelectedSlots(adminSelectedSlots.filter(s => s.getTime() !== slotTime.getTime()));
        } else {
            setAdminSelectedSlots([...adminSelectedSlots, slotTime].sort((a, b) => a.getTime() - b.getTime()));
        }
    };

    const handleToggleDay = () => {
        const isDayFullySelected = allTimeSlots.every(slotTime => {
            const statusInfo = getSlotBlockStatus(slotTime);
            const isAvailableOrBlocked = statusInfo.status === 'available' || statusInfo.status === 'blocked';
            return isAvailableOrBlocked ? adminSelectedSlots.some(s => s.getTime() === slotTime.getTime()) : true;
        });
        if (isDayFullySelected) {
            const slotTimes = allTimeSlots.map(slot => slot.getTime());
            setAdminSelectedSlots(prev => prev.filter(s => !slotTimes.includes(s.getTime())));
        } else {
            const slotsToSelect = allTimeSlots.filter(slotTime => {
                const statusInfo = getSlotBlockStatus(slotTime);
                return statusInfo.status !== 'past' && statusInfo.status !== 'booked';
            });
            const currentSelectedTimes = new Set(adminSelectedSlots.map(s => s.getTime()));
            const newUniqueSlots = slotsToSelect.filter(s => !currentSelectedTimes.has(s.getTime()));
            setAdminSelectedSlots(prev => [...prev, ...newUniqueSlots].sort((a, b) => a.getTime() - b.getTime()));
        }
    };
    
    const handleConfirmAdminBlockUnblock = async () => {
        if (adminSelectedSlots.length === 0) {
            setMessage("Please select slots.");
            setMessageType('error');
            return;
        }
        const slotsToBlock = [];
        const blockIdsToUnblock = [];
        
        adminSelectedSlots.forEach(slotTime => {
            const statusInfo = getSlotBlockStatus(slotTime);
            if (statusInfo.status === 'blocked') blockIdsToUnblock.push(statusInfo.id);
            else if (statusInfo.status === 'selected' || statusInfo.status === 'available') slotsToBlock.push(slotTime);
        });

        if (slotsToBlock.length > 0 && !blockReason.trim()) {
            setMessage("Reason required to block slots.");
            setMessageType('error');
            return;
        }

        setLoading(true);
        setMessage('');

        try {
            const slotsColRef = collection(db, 'artifacts', appId, 'public', 'data', 'blockedDays');
            const bookingsColRef = collection(db, 'artifacts', appId, 'public', 'data', 'bookings');
            const batch = writeBatch(db);
            const affectedUserRefunds = {};
            let blocksProcessed = 0;
            let unblocksProcessed = 0;

            for (const slotTime of slotsToBlock) {
                const slotStartTimeMs = slotTime.getTime();
                const newBlockDocRef = doc(slotsColRef);
                const slotEnd = new Date(slotTime);
                slotEnd.setMinutes(slotTime.getMinutes() + 30);
                batch.set(newBlockDocRef, {
                    startTime: slotTime,
                    endTime: slotEnd,
                    date: new Date(slotTime.getFullYear(), slotTime.getMonth(), slotTime.getDate()),
                    reason: blockReason.trim(),
                    blockedBy: currentUser.uid,
                    blockedAt: new Date()
                });
                blocksProcessed++;
                const conflictingBookings = bookings.filter(b => b.status === 'approved' && b.startTime.getTime() === slotStartTimeMs);
                for (const booking of conflictingBookings) {
                    const bookingDocRef = doc(bookingsColRef, booking.id);
                    batch.update(bookingDocRef, { 
                        status: 'Cancelled by Admin (Blocked)',
                        cancellationReason: blockReason.trim(),
                        cancelledAt: new Date()
                    });
                    affectedUserRefunds[booking.userId] = (affectedUserRefunds[booking.userId] || 0) + 1;
                }
            }

            for (const blockId of blockIdsToUnblock) {
                const blockDocRef = doc(slotsColRef, blockId);
                batch.delete(blockDocRef);
                unblocksProcessed++;
            }

            await batch.commit();

            const refundBatch = writeBatch(db);
            for (const [userId, spotsToRefund] of Object.entries(affectedUserRefunds)) {
                const userDocRef = doc(db, 'artifacts', appId, 'users', userId);
                const userDoc = await getDoc(userDocRef); 
                if (userDoc.exists()) {
                    const currentSpots = userDoc.data().availableSpots || 0;
                    const newSpots = currentSpots + spotsToRefund;
                    refundBatch.update(userDocRef, { availableSpots: newSpots });
                }
            }
            if (Object.keys(affectedUserRefunds).length > 0) await refundBatch.commit();

            setMessage(`Blocked ${blocksProcessed}, Unblocked ${unblocksProcessed}.`);
            setMessageType('info');
            setAdminSelectedSlots([]);
            setBlockReason('');
        } catch (error) {
            console.error("Error blocking/unblocking:", error);
            setMessage(`Error: ${error.message}`);
            setMessageType('error');
        } finally {
            setLoading(false);
        }
    };
    
    const handleDaySelect = (day) => {
        setSelectedDate(day);
        setAdminSelectedSlots([]);
    };

    const handleWeekChange = (weeks) => {
        const newWeekStart = new Date(currentWeekStart);
        newWeekStart.setDate(newWeekStart.getDate() + (weeks * 7));
        setCurrentWeekStart(newWeekStart);
        const today = new Date();
        today.setHours(0,0,0,0);
        if (newWeekStart.getTime() <= today.getTime() && new Date(newWeekStart.getTime() + 6 * 24 * 60 * 60 * 1000).getTime() >= today.getTime()) {
            setSelectedDate(today);
        } else {
            setSelectedDate(newWeekStart);
        }
        setAdminSelectedSlots([]);
        setBlockReason('');
    };
    
    useEffect(() => {
        if (!userDetails?.isAdmin || !db) return;
        const usersColRef = collection(db, 'artifacts', appId, 'users');
        const unsubscribeUsers = onSnapshot(usersColRef, (snapshot) => {
            const fetchedUsers = snapshot.docs.map(doc => {
                    const data = doc.data();
                    return { id: doc.id, ...data, displayName: getComputedDisplayName(data) };
            });
            setAllUsers(fetchedUsers);
        });
        
        const packagesColRef = collection(db, 'artifacts', appId, 'public', 'data', 'packages');
        const unsubscribePackages = onSnapshot(packagesColRef, (snapshot) => {
            setAllPackages(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        const requestsColRef = collection(db, 'artifacts', appId, 'public', 'data', 'userPackages');
        const unsubscribeRequests = onSnapshot(query(requestsColRef, where('status', '==', 'pending')), (snapshot) => {
            setPendingRequests(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), requestedAt: doc.data().requestedAt?.toDate() })));
        });
        
        const unsubscribeApprovedRequests = onSnapshot(query(requestsColRef, where('status', '==', 'approved')), (snapshot) => {
            setAllApprovedPurchases(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), requestedAt: doc.data().requestedAt?.toDate() })));
        });

        return () => {
            unsubscribeUsers();
            unsubscribePackages();
            unsubscribeRequests();
            unsubscribeApprovedRequests();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userDetails, gateCode]); 

    const handleSavePackage = async () => {
        if (!newPackageName || !newPackageSlots || !newPackageDuration || !newPackageCost) {
            setMessage("Fill all fields.");
            setMessageType("error");
            return;
        }
        setLoading(true);
        setMessage('');
        const packageData = { name: newPackageName, slots: parseInt(newPackageSlots), duration: parseInt(newPackageDuration), cost: parseFloat(newPackageCost), description: newPackageDescription };

        try {
            if (packageToEdit) {
                const packageDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'packages', packageToEdit.id);
                await updateDoc(packageDocRef, packageData);
                setMessage(`Package updated.`);
            } else {
                const packagesColRef = collection(db, 'artifacts', appId, 'public', 'data', 'packages');
                await addDoc(packagesColRef, { ...packageData, status: 'inactive', createdAt: new Date() });
                setMessage("Package created.");
            }
            setMessageType("info");
            setNewPackageName(''); setNewPackageSlots(''); setNewPackageDuration(''); setNewPackageDescription(''); setNewPackageCost(''); setPackageToEdit(null);
        } catch (error) {
            console.error("Error saving package:", error);
            setMessage(`Error: ${error.message}`);
            setMessageType("error");
        } finally {
            setLoading(false);
        }
    };

    const handleEditPackageClick = (pkg) => {
        setPackageToEdit(pkg);
        setNewPackageName(pkg.name);
        setNewPackageSlots(pkg.slots.toString());
        setNewPackageCost(pkg.cost.toString());
        setNewPackageDuration(pkg.duration.toString());
        setNewPackageDescription(pkg.description);
        setMessage('');
        // Scroll to form
        const form = document.getElementById('package-form');
        if(form) form.scrollIntoView({ behavior: 'smooth' });
    };

    const handleCancelEdit = () => {
        setPackageToEdit(null); setNewPackageName(''); setNewPackageSlots(''); setNewPackageCost(''); setNewPackageDuration(''); setNewPackageDescription(''); setMessage('');
    };

    const handleUpdatePackageStatus = async (packageId, newStatus) => {
        setLoading(true);
        try {
            const packageDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'packages', packageId);
            await updateDoc(packageDocRef, { status: newStatus });
            setMessage("Status updated.");
        } catch (error) {
            setMessage(`Error: ${error.message}`); setMessageType("error");
        } finally { setLoading(false); }
    };
    
    const handleConfirmDeletePackage = async () => {
        if (!packageToDelete || !db) return; 
        setLoading(true);
        try {
            const packageDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'packages', packageToDelete.id);
            await deleteDoc(packageDocRef);
            setMessage(`Package deleted.`);
        } catch (error) {
            setMessage(`Error: ${error.message}`); setMessageType("error");
        } finally { setLoading(false); setPackageToDelete(null); }
    };

    const handleApproveRequest = async (request) => {
        setLoading(true);
        try {
            const batch = writeBatch(db);
            const requestDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'userPackages', request.id);
            const userDocRef = doc(db, 'artifacts', appId, 'users', request.userId);
            const userDoc = await getDoc(userDocRef);
            const currentSpots = userDoc.data()?.availableSpots || 0;
            batch.update(userDocRef, { availableSpots: currentSpots + request.slotsAdded });
            batch.update(requestDocRef, { status: 'approved', approvedAt: new Date(), rejectionReason: null });
            await batch.commit();
            setMessage(`Request approved.`);
        } catch (error) {
            setMessage(`Error: ${error.message}`); setMessageType("error");
        } finally { setLoading(false); }
    };
    
    const handleRejectClick = (request) => {
        setRequestToReject(request);
        setRejectionReason(request.rejectionReason || ''); 
    };

    const handleConfirmRejectRequest = async () => {
        if (!requestToReject || !rejectionReason.trim()) {
            setMessage("Reason required."); setMessageType("error"); return;
        }
        setLoading(true);
        try {
            const requestDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'userPackages', requestToReject.id);
            await updateDoc(requestDocRef, { status: 'rejected', rejectedAt: new Date(), rejectionReason: rejectionReason.trim() });
            setMessage(`Request rejected.`);
        } catch (error) {
            setMessage(`Error: ${error.message}`); setMessageType("error");
        } finally { setLoading(false); setRequestToReject(null); setRejectionReason(''); }
    };

    const handleToggleBookings = async () => {
        if (!userDetails?.isAdmin) return;
        setLoading(true);
        try {
            const settingsRef = doc(db, 'artifacts', appId, 'public', 'appSettings');
            await setDoc(settingsRef, { bookingsDisabled: !bookingsDisabled }, { merge: true });
            setBookingsDisabled(!bookingsDisabled);
            setMessage(`Bookings ${!bookingsDisabled ? 'disabled' : 'enabled'}.`);
        } catch (error) {
            setMessage(`Error: ${error.message}`); setMessageType('error');
        } finally { setLoading(false); }
    };
    
    const handleUpdateGateCode = async () => {
        if (!userDetails?.isAdmin || !db) return;
        setLoading(true);
        try {
            const settingsRef = doc(db, 'artifacts', appId, 'public', 'appSettings');
            await setDoc(settingsRef, { gateCode: newGateCode }, { merge: true });
            setMessage(`Gate code updated.`);
        } catch (error) {
            setMessage(`Error: ${error.message}`); setMessageType('error');
        } finally { setLoading(false); }
    };
    
    if (!userDetails?.isAdmin) {
        return <div className="p-4 text-center text-red-800">Authorized Access Only.</div>;
    }

    return (
        <div className="container mx-auto p-6 bg-white rounded-lg shadow-lg my-6">
            <MessageBox message={message} type={messageType} onClose={() => setMessage('')} />
            
            {packageToDelete && (
                <MessageBox
                    message={`Permanently delete "${packageToDelete.name}"?`}
                    onClose={() => setPackageToDelete(null)}
                    type="error"
                >
                    <div className="mt-4 flex justify-center space-x-4">
                        <button onClick={handleConfirmDeletePackage} className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700">Confirm</button>
                        <button onClick={() => setPackageToDelete(null)} className="px-6 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400">Cancel</button>
                    </div>
                </MessageBox>
            )}
            
            {requestToReject && (
                    <MessageBox
                    message={`Rejection reason for ${requestToReject.userName}:`}
                    onClose={() => setRequestToReject(null)}
                    type="error"
                    >
                    <div className="mt-4 w-full">
                        <textarea
                            value={rejectionReason}
                            onChange={(e) => setRejectionReason(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none"
                            rows="3"
                        />
                        <div className="mt-4 flex justify-center space-x-4">
                            <button onClick={handleConfirmRejectRequest} disabled={!rejectionReason.trim() || loading} className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50">Confirm</button>
                            <button onClick={() => setRequestToReject(null)} className="px-6 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400">Cancel</button>
                        </div>
                    </div>
                </MessageBox>
            )}

            <h2 className="font-display text-4xl sm:text-5xl text-gray-800 mb-6 text-center tracking-wider">Admin Dashboard</h2>
            
            {/* Quick Nav Bar */}
            <div className="mb-8 flex flex-wrap justify-center gap-2 sticky top-0 bg-white z-10 p-2 shadow-sm border-b">
                <span className="text-sm font-bold text-gray-500 self-center mr-2">Quick Jump:</span>
                <button onClick={() => scrollToSection('section-pending-bookings')} className="px-3 py-1 bg-gray-100 hover:bg-indigo-100 text-indigo-700 rounded-full text-xs font-semibold">Bookings</button>
                <button onClick={() => scrollToSection('section-pending-packages')} className="px-3 py-1 bg-gray-100 hover:bg-purple-100 text-purple-700 rounded-full text-xs font-semibold">Requests</button>
                <button onClick={() => scrollToSection('section-user-lookup')} className="px-3 py-1 bg-gray-100 hover:bg-yellow-100 text-yellow-700 rounded-full text-xs font-semibold">User Lookup</button>
                <button onClick={() => scrollToSection('section-block-dates')} className="px-3 py-1 bg-gray-100 hover:bg-red-100 text-red-700 rounded-full text-xs font-semibold">Block Dates</button>
                <button onClick={() => scrollToSection('section-manage-packages')} className="px-3 py-1 bg-gray-100 hover:bg-purple-100 text-purple-700 rounded-full text-xs font-semibold">Packages</button>
                <button onClick={() => scrollToSection('section-settings')} className="px-3 py-1 bg-gray-100 hover:bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">Settings</button>
                <button onClick={() => scrollToSection('section-user-spots')} className="px-3 py-1 bg-gray-100 hover:bg-yellow-100 text-yellow-700 rounded-full text-xs font-semibold">Spots/Status</button>
                <button onClick={() => scrollToSection('section-shop')} className="px-3 py-1 bg-gray-100 hover:bg-emerald-100 text-emerald-700 rounded-full text-xs font-semibold">Shop</button>
            </div>
            
            <div id="section-pending-bookings" className="mb-8 p-6 border border-gray-200 rounded-lg shadow-sm bg-indigo-50 scroll-mt-24">
                <h3 className="text-2xl font-bold text-indigo-800 mb-4 text-center">Admin: Pending Bookings</h3>
                {pendingBookings.length === 0 ? <p className="text-center text-gray-600">No pending bookings.</p> : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full bg-white rounded-lg shadow-md">
                            <thead className="bg-gray-100 border-b border-gray-200">
                                <tr><th className="py-3 px-4 text-left text-sm font-semibold text-gray-600">User</th><th className="py-3 px-4 text-left text-sm font-semibold text-gray-600">Time</th><th className="py-3 px-4 text-left text-sm font-semibold text-gray-600">Actions</th></tr>
                            </thead>
                            <tbody>
                                {pendingBookings.map(booking => (
                                    <tr key={booking.id} className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50">
                                        <td className="py-3 px-4 text-gray-800 text-sm" style={{ color: booking.userColor }}>{booking.userName}</td>
                                        <td className="py-3 px-4 text-gray-800 text-sm">{booking.startTime.toLocaleString()}</td>
                                        <td className="py-3 px-4 flex space-x-2">
                                            <button onClick={() => handleBookingAction(booking.id, 'approved')} className="px-4 py-2 bg-green-500 text-white rounded-md text-sm hover:bg-green-600">Approve</button>
                                            <button onClick={() => handleBookingAction(booking.id, 'rejected')} className="px-4 py-2 bg-red-500 text-white rounded-md text-sm hover:bg-red-600">Reject</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            
            <div id="section-pending-packages" className="mb-8 p-6 border border-gray-200 rounded-lg shadow-sm bg-purple-50 scroll-mt-24">
                <h3 className="text-2xl font-bold text-purple-800 mb-4 text-center">Admin: Pending Package Requests</h3>
                {pendingRequests.length === 0 ? <p className="text-center text-gray-600">No pending requests.</p> : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full bg-white rounded-lg shadow-md">
                            <thead className="bg-gray-100 border-b border-gray-200">
                                <tr><th className="py-3 px-4 text-left text-sm font-semibold text-gray-600">User</th><th className="py-3 px-4 text-left text-sm font-semibold text-gray-600">Package</th><th className="py-3 px-4 text-left text-sm font-semibold text-gray-600">Actions</th></tr>
                            </thead>
                            <tbody>
                                {pendingRequests.map(req => (
                                    <tr key={req.id} className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50">
                                        <td className="py-3 px-4 text-gray-800 text-sm">{req.userName}</td>
                                        <td className="py-3 px-4 text-gray-800 text-sm">{req.packageName}</td>
                                        <td className="py-3 px-4 flex space-x-2">
                                            <button onClick={() => handleApproveRequest(req)} className="px-4 py-2 bg-green-500 text-white rounded-md text-sm hover:bg-green-600">Approve</button>
                                            <button onClick={() => handleRejectClick(req)} className="px-4 py-2 bg-red-500 text-white rounded-md text-sm hover:bg-red-600">Reject</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <hr className="my-8 border-gray-300" />
            <AdminUserLookup allUsers={allUsers} />
            <hr className="my-8 border-gray-300" />
            
            <div id="section-block-dates" className="mb-8 p-6 border border-red-300 rounded-lg shadow-sm bg-red-50 scroll-mt-24">
                <h3 className="text-2xl font-bold text-red-800 mb-4 text-center">Admin: Block/Unblock Time Slots</h3>
                <div className="flex justify-center items-center space-x-4 mb-4">
                    <button onClick={() => handleWeekChange(-1)} className="p-2 rounded-full bg-red-500 text-white shadow-md">&lt;</button>
                    <span className="text-xl font-semibold text-gray-700">{currentWeekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                    <button onClick={() => handleWeekChange(1)} className="p-2 rounded-full bg-red-500 text-white shadow-md">&gt;</button>
                </div>
                
                <div className="flex justify-center space-x-1 mb-6 sm:space-x-2">
                    {weekDays.map(day => (
                        <button key={day.toISOString()} onClick={() => handleDaySelect(day)} className={`flex flex-col items-center p-1 rounded-md text-xs font-semibold sm:px-4 sm:py-2 ${selectedDate.toDateString() === day.toDateString() ? 'bg-red-500 text-white' : 'bg-white text-gray-700 border'}`}>
                            <span className="block">{day.toLocaleDateString('en-US', { weekday: 'short' })}</span>
                            <span className="block font-bold text-sm">{day.getDate()}</span>
                        </button>
                    ))}
                </div>

                <div className="mb-4 p-4 border border-gray-200 rounded-lg bg-white">
                    <div className="flex justify-between items-center mb-3">
                        <h4 className="text-xl font-semibold text-red-700">{selectedDate.toLocaleDateString()}</h4>
                        <button onClick={handleToggleDay} className="px-4 py-2 bg-red-100 text-red-800 rounded-md text-sm">Select All</button>
                    </div>
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3 mb-4">
                        {allTimeSlots.map((slotTime, index) => {
                            const statusInfo = getSlotBlockStatus(slotTime);
                            
                            const isSelected = adminSelectedSlots.some(s => s.getTime() === slotTime.getTime());

                            let slotClasses = "p-2 rounded-lg text-center font-medium border-2 text-sm flex flex-col items-center justify-center min-h-[60px] ";
                            
                            if (statusInfo.status === 'past' || statusInfo.status === 'booked') {
                                slotClasses += "bg-gray-200 text-gray-500 line-through cursor-not-allowed";
                            } 
                            else if (isSelected) {
                                slotClasses += "bg-yellow-400 text-gray-900 border-yellow-600";
                            } 
                            else if (statusInfo.status === 'blocked') {
                                slotClasses += "bg-red-600 text-white border-red-800";
                            } 
                            else {
                                slotClasses += "bg-green-100 text-green-800 hover:bg-green-200 border-green-300 cursor-pointer";
                            }

                            return (
                                <div key={index} className={slotClasses} onClick={() => handleAdminSlotClick(slotTime)} title={statusInfo.reason}>
                                    <span className="leading-none">
                                        {slotTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                    
                                    {statusInfo.status === 'blocked' && (
                                        <span className="block text-[10px] mt-1 font-normal opacity-90 leading-tight break-words w-full overflow-hidden text-ellipsis px-1">
                                            {statusInfo.reason}
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    <textarea value={blockReason} onChange={(e) => setBlockReason(e.target.value)} placeholder="Reason for Blocking" className="w-full px-4 py-2 border rounded-lg mb-2" rows="2"></textarea>
                    <button onClick={handleConfirmAdminBlockUnblock} disabled={loading} className="w-full py-3 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 disabled:opacity-50">Submit Action</button>
                </div>
            </div>

            <hr className="my-8 border-gray-300" />
            
            <div id="section-manage-packages" className="mb-8 p-6 border border-gray-200 rounded-lg shadow-sm bg-purple-50 scroll-mt-24">
                <h3 className="text-2xl font-bold text-purple-800 mb-4 text-center">Admin: Package Management</h3>
                <div id="package-form" className="space-y-4 p-4 border border-purple-200 rounded-lg bg-white mb-6">
                    <h4 className="text-xl font-semibold text-purple-700">{packageToEdit ? `Edit: ${packageToEdit.name}` : 'New Package'}</h4>
                    <div className="grid grid-cols-2 gap-4">
                        <input type="text" value={newPackageName} onChange={(e) => setNewPackageName(e.target.value)} placeholder="Name" className="px-4 py-2 border rounded-lg" />
                        <input type="number" value={newPackageSlots} onChange={(e) => setNewPackageSlots(e.target.value)} placeholder="Slots" className="px-4 py-2 border rounded-lg" />
                        <input type="number" value={newPackageCost} onChange={(e) => setNewPackageCost(e.target.value)} placeholder="Cost (R)" step="0.01" className="px-4 py-2 border rounded-lg" />
                        <input type="number" value={newPackageDuration} onChange={(e) => setNewPackageDuration(e.target.value)} placeholder="Duration (Days)" className="px-4 py-2 border rounded-lg" />
                    </div>
                    <textarea value={newPackageDescription} onChange={(e) => setNewPackageDescription(e.target.value)} placeholder="Description" className="w-full px-4 py-2 border rounded-lg"></textarea>
                    <div className="flex space-x-2">
                        <button onClick={handleSavePackage} disabled={loading} className="flex-1 py-3 bg-green-600 text-white font-bold rounded-lg hover:bg-green-700">{packageToEdit ? 'Save' : 'Create'}</button>
                        {packageToEdit && <button onClick={handleCancelEdit} className="flex-1 py-3 bg-gray-400 text-white font-bold rounded-lg hover:bg-gray-500">Cancel</button>}
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {allPackages.map(pkg => (
                        <div key={pkg.id} className="p-4 border border-gray-200 rounded-lg bg-white">
                            <div className="flex justify-between font-bold text-lg"><span>{pkg.name}</span><span className="text-xs font-normal bg-gray-100 px-2 py-1 rounded">{pkg.status}</span></div>
                            <p className="text-green-700 font-bold">R {pkg.cost?.toFixed(2)}</p>
                            <p className="text-sm">{pkg.slots} slots | {pkg.duration} days</p>
                            <div className="mt-2 flex space-x-2 text-sm">
                                <button onClick={() => handleEditPackageClick(pkg)} className="text-yellow-600 hover:underline">Edit</button>
                                <button onClick={() => handleUpdatePackageStatus(pkg.id, pkg.status === 'active' ? 'inactive' : 'active')} className="text-blue-600 hover:underline">{pkg.status === 'active' ? 'Deactivate' : 'Activate'}</button>
                                <button onClick={() => setPackageToDelete(pkg)} className="text-red-600 hover:underline">Delete</button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            
            <hr className="my-8 border-gray-300" />
            <div id="section-settings" className="flex flex-col lg:flex-row space-y-8 lg:space-y-0 lg:space-x-8 mb-8 scroll-mt-24">
                <div className="flex-1 p-6 border border-gray-200 rounded-lg shadow-sm bg-purple-50">
                    <h3 className="text-2xl font-bold text-purple-800 mb-4 text-center">Gate Code</h3>
                    <input type="text" value={newGateCode} onChange={(e) => setNewGateCode(e.target.value)} className="w-full px-4 py-2 border rounded-lg text-lg font-mono mb-2" placeholder={gateCode || "Set Code"} />
                    <button onClick={handleUpdateGateCode} disabled={loading} className="w-full py-2 bg-purple-600 text-white font-bold rounded-lg hover:bg-purple-700">Update Code</button>
                </div>
                <div className="flex-1 p-6 border border-gray-200 rounded-lg shadow-sm bg-blue-50">
                    <h3 className="text-2xl font-bold text-blue-800 mb-4 text-center">Global Settings</h3>
                    <div className="flex justify-between items-center">
                        <span className="text-lg font-medium text-gray-700">Disable All Bookings</span>
                        <input type="checkbox" checked={bookingsDisabled} onChange={handleToggleBookings} className="h-6 w-6" />
                    </div>
                </div>
            </div>

            <hr className="my-8 border-gray-300" />
            <ManageUserSpots />
            <hr className="my-8 border-gray-300" />
            <AdminShopManager />
        </div>
    );
}

export default BlockDatesPage;